import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import { categories } from "@/mocks/categories";

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://superbuy.sbs';

const guideJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The Ultimate Superbuy Spreadsheet Guide for Smart Shoppers in 2026",
    "description": "Everything you need to know about finding, organizing, and maximizing the best superbuy spreadsheet finds — from trusted seller verification to shipping optimization and price comparison strategies.",
    "author": { "@type": "Organization", "name": "Superbuy Spreadsheet" },
    "publisher": { "@type": "Organization", "name": "Superbuy Spreadsheet", "url": siteUrl },
    "datePublished": "2026-06-01",
    "dateModified": "2026-06-21",
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}/superbuy-spreadsheet-guide` },
    "image": "https://readdy.ai/api/search-image?query=Warm%20minimalist%20overhead%20photography%20of%20an%20open%20laptop%20with%20a%20neatly%20organized%20product%20spreadsheet%20on%20screen%20surrounded%20by%20a%20steaming%20ceramic%20coffee%20mug%20a%20small%20notebook%20with%20handwritten%20notes%20and%20a%20tiny%20potted%20fern%20on%20a%20light%20walnut%20wooden%20desk%2C%20gentle%20morning%20sunlight%20streaming%20through%20sheer%20linen%20curtains%2C%20warm%20oatmeal%20walls%2C%20editorial%20productivity%20photography%2C%20calm%20and%20organized%20atmosphere%2C%20soft%20shadows&width=1200&height=600&seq=article-hero-guide-v2&orientation=landscape"
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
      { "@type": "ListItem", "position": 2, "name": "Superbuy Spreadsheet Complete Guide 2026", "item": `${siteUrl}/superbuy-spreadsheet-guide` }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Is the superbuy spreadsheet free to use?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, all our spreadsheets are completely free. We believe in making quality shopping resources accessible to everyone. Simply visit our main website to access the latest versions." } },
      { "@type": "Question", "name": "How often is the spreadsheet updated?", "acceptedAnswer": { "@type": "Answer", "text": "Our spreadsheets are updated weekly with new product finds, refreshed seller ratings, and current pricing information. Major updates occur at least once per month with comprehensive data refreshes." } },
      { "@type": "Question", "name": "Can I trust the seller ratings in the spreadsheet?", "acceptedAnswer": { "@type": "Answer", "text": "Seller ratings are compiled from verified buyer reviews, platform data, and community feedback. While we strive for accuracy, we always recommend checking recent reviews and QC photos before placing any order." } },
      { "@type": "Question", "name": "What categories are covered in the spreadsheet?", "acceptedAnswer": { "@type": "Answer", "text": "We cover 15 major categories including Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and more. Each category contains dozens of curated listings." } },
      { "@type": "Question", "name": "How do I get started with my first order?", "acceptedAnswer": { "@type": "Answer", "text": "Start by browsing the spreadsheet for items that interest you. Check the seller rating and any available QC photos. Then visit our main website to place your order through the secure platform. Our step-by-step guides will walk you through the entire process." } }
    ]
  }
];

export default function SpreadsheetGuidePage() {
  const navigate = useNavigate();

  return (
    <>
      <SEOHead
        title="The Ultimate Superbuy Spreadsheet Guide for Smart Shoppers 2026"
        description="Everything you need to know about finding, organizing, and maximizing the best superbuy spreadsheet finds — from trusted seller verification to shipping optimization and price comparison strategies."
        keywords="superbuy spreadsheet guide, how to use superbuy spreadsheet, superbuy finds guide, superbuy shopping tips"
        canonicalPath="/superbuy-spreadsheet-guide"
        ogType="article"
        jsonLd={guideJsonLd}
      />
      <Navbar />
      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="px-6 md:px-12 py-4 bg-background-100">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <button onClick={() => navigate("/")} className="hover:text-primary-600 transition-colors cursor-pointer">
              Home
            </button>
            <span>/</span>
            <span className="text-foreground-800 font-medium">Superbuy Spreadsheet Complete Guide 2026</span>
          </nav>
        </div>

        <article className="px-6 md:px-12 py-10 md:py-14 max-w-4xl mx-auto">
          {/* Article Header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">Guide</span>
              <span className="text-xs text-foreground-500">Updated June 2026</span>
              <span className="text-xs text-foreground-500">12 min read</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
              The Ultimate Superbuy Spreadsheet Guide for Smart Shoppers in 2026
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
              Everything you need to know about finding, organizing, and maximizing the best superbuy spreadsheet finds &mdash;
              from trusted seller verification to shipping optimization and price comparison strategies.
            </p>
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20overhead%20photography%20of%20an%20open%20laptop%20with%20a%20neatly%20organized%20product%20spreadsheet%20on%20screen%20surrounded%20by%20a%20steaming%20ceramic%20coffee%20mug%20a%20small%20notebook%20with%20handwritten%20notes%20and%20a%20tiny%20potted%20fern%20on%20a%20light%20walnut%20wooden%20desk%2C%20gentle%20morning%20sunlight%20streaming%20through%20sheer%20linen%20curtains%2C%20warm%20oatmeal%20walls%2C%20editorial%20productivity%20photography%2C%20calm%20and%20organized%20atmosphere%2C%20soft%20shadows&width=1200&height=600&seq=article-hero-guide-v2&orientation=landscape"
              alt="Superbuy spreadsheet guide - organized workspace with laptop and notes"
              className="w-full rounded-lg mt-8"
            />
          </header>

          {/* Article Content */}
          <div className="prose-custom space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                What Is a Superbuy Spreadsheet and Why You Need One
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                A <strong className="text-foreground-900">superbuy spreadsheet</strong> is a meticulously organized digital document that compiles
                product listings, seller ratings, price comparisons, shipping estimates, and quality control notes for items available
                through superbuy and similar shopping platforms. For anyone who regularly shops from overseas markets, having a reliable
                <strong className="text-foreground-900">superbuy spreadsheet</strong> is not just convenient &mdash; it is essential for making informed
                purchasing decisions while avoiding common pitfalls.
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                The concept originated within online shopping communities where experienced buyers began sharing their personal tracking
                sheets. Over time, these evolved into comprehensive, community-driven resources that now help thousands of shoppers every month.
                A well-maintained <strong className="text-foreground-900">superbuy spreadsheet</strong> typically includes product links, size charts,
                seller trust scores, real buyer review excerpts, and estimated total costs including international shipping.
              </p>
              <p className="text-foreground-700 leading-relaxed">
                Whether you are hunting for limited-edition sneakers, premium streetwear, luxury accessories, or everyday essentials at
                unbeatable prices, the right spreadsheet transforms hours of scattered browsing into an efficient, confidence-boosting
                shopping workflow. In this guide we cover everything from getting started to advanced optimization techniques.
              </p>
            </section>

            {/* CTA */}
            <div className="bg-accent-50 border border-accent-200 rounded-lg p-6 text-center">
              <p className="text-sm font-semibold text-accent-800 mb-3">
                Ready to start shopping? Visit our main website for the latest spreadsheet updates.
              </p>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Browse superbuy spreadsheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            {/* Benefits */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Key Benefits of Using a Superbuy Spreadsheet
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { title: "Time Savings", desc: "Cut research time by 70% with pre-vetted product listings and organized categories." },
                  { title: "Cost Comparison", desc: "Compare prices across multiple sellers instantly to find the best deals." },
                  { title: "Risk Reduction", desc: "Access verified seller ratings and real QC photos before you commit to a purchase." },
                  { title: "Shipping Optimization", desc: "Calculate estimated shipping costs upfront and choose the most economical routes." },
                ].map((item) => (
                  <div key={item.title} className="bg-background-100 rounded-lg p-5">
                    <h3 className="font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Comparison Table */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Superbuy Spreadsheet vs Other Methods
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Feature</th>
                      <th className="text-left py-3 px-4 font-semibold text-accent-700 bg-accent-50">Superbuy Spreadsheet</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-700">Manual Browsing</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-700">Social Media Groups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Product Selection", "Curated &amp; Verified", "Unfiltered &amp; Random", "Depends on Group"],
                      ["Seller Trust Score", "Included with Reviews", "Hard to Verify", "Anecdotal Only"],
                      ["Price Comparison", "Side-by-Side Tables", "Manual Tab Switching", "Screenshot-Based"],
                      ["QC Photo Access", "Linked Directly", "Must Request Manually", "Rarely Available"],
                      ["Shipping Estimates", "Pre-Calculated", "Guesswork", "Inconsistent"],
                      ["Update Frequency", "Weekly / Daily", "N/A", "Whenever Posted"],
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-background-100">
                        <td className="py-3 px-4 font-medium text-foreground-800">{row[0]}</td>
                        <td className="py-3 px-4 text-accent-700 bg-accent-50/50">{row[1]}</td>
                        <td className="py-3 px-4 text-foreground-600">{row[2]}</td>
                        <td className="py-3 px-4 text-foreground-600">{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Safety Section */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Safety Guide: How to Use Your Spreadsheet Securely
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                Security should always be your top priority when shopping through any platform. A quality
                <strong className="text-foreground-900">superbuy spreadsheet</strong> includes safety indicators that help you identify trusted
                sellers and avoid potential scams. Here are the key safety practices to follow:
              </p>
              <ul className="space-y-3 ml-5 list-disc text-foreground-700">
                <li><strong className="text-foreground-900">Verify Seller Ratings:</strong> Always check the seller trust score column before placing any order. Stick to sellers with 95% positive ratings or higher.</li>
                <li><strong className="text-foreground-900">Review QC Photos:</strong> Quality control inspection photos are your best defense against receiving incorrect or damaged items. Make sure your spreadsheet includes QC photo links.</li>
                <li><strong className="text-foreground-900">Use Secure Payment Methods:</strong> Only use the platform&rsquo;s official payment channels. Never send money directly to individual sellers outside the protected system.</li>
                <li><strong className="text-foreground-900">Check Shipping Insurance:</strong> Confirm whether shipping insurance is included in the estimated cost. For high-value items, always opt for insured shipping.</li>
                <li><strong className="text-foreground-900">Understand Customs Policies:</strong> Your spreadsheet should include notes on customs duties and import restrictions for your destination country.</li>
              </ul>
            </section>

            {/* How to Use */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                How to Get the Most Out of Your Superbuy Spreadsheet
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-foreground-900 mb-2">Step 1: Choose Your Categories</h3>
                  <p className="text-foreground-700 leading-relaxed">
                    Start by identifying which product categories matter most to you. Our spreadsheets are organized into sections
                    including <button onClick={() => navigate("/")} className="text-accent-600 hover:text-accent-700 underline cursor-pointer">Shoes</button>,
                    Hoodies, Jackets, Bags, Accessories, and more &mdash; so you can jump straight to what you need without wading through irrelevant listings.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-900 mb-2">Step 2: Sort by Priority</h3>
                  <p className="text-foreground-700 leading-relaxed">
                    Use the built-in sorting columns to rank items by price, seller rating, or shipping speed. This lets you quickly
                    identify the best value purchases and avoid overpaying for items that are available from multiple sellers at different prices.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-900 mb-2">Step 3: Track Your Orders</h3>
                  <p className="text-foreground-700 leading-relaxed">
                    Once you start ordering, use the spreadsheet to track each item&rsquo;s status from purchase through QC inspection,
                    warehouse storage, and final international shipping. Having everything in one place prevents lost orders and missed delivery windows.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-900 mb-2">Step 4: Share and Contribute</h3>
                  <p className="text-foreground-700 leading-relaxed">
                    The best spreadsheets are community-driven. Share your finds and seller experiences with the community.
                    Visit our <button onClick={() => navigate("/")} className="text-accent-600 hover:text-accent-700 underline cursor-pointer">homepage</button> for
                    the latest community updates and new spreadsheet releases.
                  </p>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {[
                  {
                    q: "Is the superbuy spreadsheet free to use?",
                    a: "Yes, all our spreadsheets are completely free. We believe in making quality shopping resources accessible to everyone. Simply visit our main website to access the latest versions.",
                  },
                  {
                    q: "How often is the spreadsheet updated?",
                    a: "Our spreadsheets are updated weekly with new product finds, refreshed seller ratings, and current pricing information. Major updates occur at least once per month with comprehensive data refreshes.",
                  },
                  {
                    q: "Can I trust the seller ratings in the spreadsheet?",
                    a: "Seller ratings are compiled from verified buyer reviews, platform data, and community feedback. While we strive for accuracy, we always recommend checking recent reviews and QC photos before placing any order.",
                  },
                  {
                    q: "What categories are covered in the spreadsheet?",
                    a: "We cover 15 major categories including Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and more. Each category contains dozens of curated listings.",
                  },
                  {
                    q: "How do I get started with my first order?",
                    a: "Start by browsing the spreadsheet for items that interest you. Check the seller rating and any available QC photos. Then visit our main website to place your order through the secure platform. Our step-by-step guides will walk you through the entire process.",
                  },
                ].map((faq, i) => (
                  <details key={i} className="bg-background-100 rounded-lg group">
                    <summary className="px-5 py-4 font-medium text-foreground-900 cursor-pointer list-none flex items-center justify-between">
                      {faq.q}
                      <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                      <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Internal Links */}
            <section className="bg-background-100 rounded-lg p-6">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                Explore More Resources
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => navigate("/")} className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer text-left">
                  <i className="ri-home-4-line text-accent-500"></i>
                  Back to Homepage
                </button>
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-external-link-line text-accent-500"></i>
                  Visit Main Website
                </a>
              </div>
            </section>

            {/* Product Categories Quick Links */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Browse Product Categories
              </h2>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-4 py-2 bg-background-100 hover:bg-accent-100 text-sm text-foreground-700 hover:text-accent-800 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </section>

            {/* Final CTA */}
            <div className="text-center py-8 bg-primary-500 rounded-lg">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-3">
                Start Shopping Smarter Today
              </h2>
              <p className="text-background-50/80 text-sm mb-5 max-w-md mx-auto">
                Access the complete superbuy spreadsheet with thousands of curated finds, trusted sellers, and real buyer reviews.
              </p>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 font-semibold text-sm rounded-full hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                superbuy spreadsheet
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}